/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

/* eslint-disable @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return --
 * The questionnaire bundle is loose-typed (display shape of the server's JSON). */

import React, { ReactElement, useState } from "react"
import { Tooltip } from "@mui/material"

/**
 * Patient Context strip — the exam workspace's `#examQuestionnaire` block
 * (handoff v0.8.3 `.exam-questionnaire`): the "Patient Context" bar with its
 * single "N responses" chip (count of yes / not-sure answers), the chevron
 * toggle, and the collapsible detail rows (short display label · answer,
 * "Not answered" for nulls). Hidden entirely when the exam carries no
 * questionnaire. Styling comes from pcg-exam-workspace.css.
 *
 * The handoff's short display labels (patientContextLabels, keyed by
 * question id) are carried verbatim; unknown ids fall back to the section
 * title, as upstream.
 *
 * Upstream's zero-flag chip tooltip dereferences an undeclared variable;
 * this component implements the intended fallbacks ("All answered responses
 * were No" / "Questionnaire not answered").
 */

const PATIENT_CONTEXT_LABELS: { [questionId: string]: string } = {
  heart_rhythm: "Irregular, racing, fluttering or skipped heartbeat",
  chest_discomfort: "Chest pain, pressure or tightness",
  shortness_of_breath: "Unexplained shortness of breath",
  fatigue_reduced_activity: "Fatigue or reduced activity",
  dizziness_fainting: "Unexplained dizziness, near-fainting or fainting",
  swelling: "Swelling in feet, ankles or lower legs",
  heart_conditions: "History of heart murmur or another heart condition",
  // The platform's v1 key is singular (questionnaire_v1.py); same label
  heart_condition: "History of heart murmur or another heart condition",
  family_history: "Family history of heart disease",
  // Platform questionnaire v2 additions (questionnaire_v2.py). Height arrives
  // as one merged section keyed by its first question.
  height_ft: "Height",
  height_in: "Height",
  weight_lbs: "Weight",
  race_ethnicity: "Race and ethnicity",
  race_ethnicity_other: "Another race or ethnicity",
  high_blood_pressure: "History of high blood pressure (hypertension)",
}

const PatientContextStrip = ({ questionnaire }: { questionnaire: any }): ReactElement | null => {
  const [open, setOpen] = useState(false)
  if (!questionnaire || !Array.isArray(questionnaire.sections) || questionnaire.sections.length === 0) {
    return null
  }

  const responses = questionnaire.sections.flatMap((section: any, index: number) =>
    (section.items ?? []).map((item: any, itemIndex: number) => ({
      // Section ids repeat across versions and titles repeat within one
      // (v2 "Race and Ethnicity" has two rows) — position keeps keys unique
      key: `${String(section.id ?? section.title ?? "")}-${index}-${itemIndex}`,
      section: String(section.title ?? ""),
      display: PATIENT_CONTEXT_LABELS[String(section.id ?? "")] || String(section.title ?? "") || String(item.q ?? ""),
      answer: item.a == null ? null : String(item.a),
    }))
  )
  const highlighted = responses
    .filter((item: any) => ["yes", "not sure"].includes(String(item.answer ?? "").toLowerCase()))
    .sort(
      (a: any, b: any) =>
        (String(a.answer).toLowerCase() === "yes" ? 0 : 1) - (String(b.answer).toLowerCase() === "yes" ? 0 : 1)
    )
  const anyAnswered = responses.some((item: any) => item.answer != null)
  const summary = `${highlighted.length} response${highlighted.length === 1 ? "" : "s"}`
  const reportedCategories = highlighted.map((item: any) => `${item.section}: ${item.answer}`).join(" · ")
  const chipTitle =
    reportedCategories || (anyAnswered ? "All answered responses were No" : "Questionnaire not answered")

  return (
    <div
      className="exam-questionnaire"
      title="Patient reported · not independently verified"
      data-testid="patient-context-strip"
    >
      <button
        type="button"
        className="exam-questionnaire-bar"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <span className="exam-questionnaire-title">Patient Context</span>
        <span className="exam-questionnaire-chips">
          <Tooltip title={chipTitle}>
            <span className="exam-questionnaire-chip">{summary}</span>
          </Tooltip>
        </span>
        <span className={`exam-questionnaire-more${open ? " open" : ""}`} aria-hidden="true" />
      </button>
      <div className={`exam-questionnaire-detail${open ? " open" : ""}`}>
        {responses.map((item: any) => (
          <div key={item.key} className="exam-questionnaire-row">
            <span>{item.display}</span>
            <span className="exam-questionnaire-answer">{item.answer ?? "Not answered"}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

export default PatientContextStrip
